import type { CesiumViewer, CVTilesLayer } from '@aus-platform/cesium';
import { MVTImageryProvider } from '@aus-platform/cesium';
import {
  Cesium3DTileset,
  CesiumTerrainProvider,
  Color,
  EllipsoidTerrainProvider,
  MapboxStyleImageryProvider,
  Rectangle,
  Resource,
  UrlTemplateImageryProvider,
  type ImageryLayer,
  type ImageryProvider,
  type TerrainProvider,
} from 'cesium';
import { cloneDeep, isEmpty, isNil, isNull, isUndefined } from 'lodash';
import {
  convertBoundingSphereToBounds,
  getLayerMetadata,
  type WorkspaceLayer,
} from 'map-3d/components';
import type { FileDataType, LayerNode } from 'src/shared/api';
import { FileType, getAuthHeader, LayerType } from 'src/shared/api';
import { CesiumBase } from 'src/shared/cesium/base';
import { buildNodeStylesMap } from 'src/shared/cesium/helpers';
import { type MapLayer } from 'src/shared/cesium/types';
import { EnvVariables } from 'src/shared/env-variables';
import { applyHiddenFeatureFiltersToStyle } from 'src/shared/helpers';
import type { HistogramDataType } from 'src/shared/types';
import { type SwipeMapLayer } from 'src/swipe-map-3d';
import type {
  AddMapLayerParams,
  ICesiumLayerManager,
  ImageryLayerMetadata,
  ImageryLayerParams,
  TilesetWithId,
} from './types';

export class CesiumLayerManager
  extends CesiumBase
  implements ICesiumLayerManager
{
  constructor(viewer: CesiumViewer) {
    super(viewer);
  }

  async addTerrain(
    iterationId: string,
    file_key: string,
  ): Promise<CesiumTerrainProvider | undefined> {
    try {
      const baseUrl = !isUndefined(EnvVariables.tileServerUrl)
        ? `${EnvVariables.tileServerUrl}/terrain/`
        : `${EnvVariables.terrainServerUrl}/`;

      const terrainResource = new Resource({
        url: baseUrl,
        headers: {
          ...getAuthHeader(),
        },
        queryParameters: { iteration_id: iterationId, key: file_key },
      });

      const terrainProvider = await CesiumTerrainProvider.fromUrl(
        terrainResource,
        {
          requestVertexNormals: true,
        },
      );

      this.baseViewer.terrainProvider = terrainProvider;
      return terrainProvider;
    } catch (error) {
      console.error('Error adding terrain:', error);
    }
  }

  async addPointCloudLayer(
    tilesetS3Key?: string,
    layerId?: string,
    imageryLayerParams?: ImageryLayerParams,
  ): Promise<Cesium3DTileset | undefined> {
    try {
      const resource = new Resource({
        url: `${EnvVariables.tileServerUrl}/3dtiles/tileset.json`,
        headers: {
          ...getAuthHeader(),
        },
        queryParameters: { key: tilesetS3Key },
      });

      const { pointCloudTool } = this.baseViewer.pointCloudTools;
      pointCloudTool.activatePointCloud();
      const tileset =
        await pointCloudTool.pointCloud.loadPointCloudTileSet(resource);

      // Set tileset ID to match workspace layer ID.
      if (tileset && layerId) {
        (tileset as TilesetWithId).id = layerId;
        pointCloudTool.pointCloud.addPointCloud(tileset, this.baseViewer);
        tileset.show = imageryLayerParams?.show ?? true;
      }

      return tileset;
    } catch (error) {
      console.error('Error adding point cloud layer:', error);
    }
  }

  // Zoom to point cloud layer
  zoomToPointCloudLayer(tileset: Cesium3DTileset) {
    try {
      // Convert bounding sphere to rectangle for 2D-style viewing
      const rectangle = this.getPointCloudRectangle(tileset);

      // Use camera flyTo with rectangle and no tilt
      if (rectangle) {
        this.baseViewer.camera.flyTo({
          destination: rectangle,
          duration: 1,
        });
      }
    } catch (error) {
      console.error('Error zooming to point cloud layer:', error);
      // Fallback to default zoom
      this.baseViewer.zoomTo(tileset);
    }
  }

  getPointCloudRectangle(tileset: Cesium3DTileset) {
    try {
      if (!tileset || !tileset.boundingSphere) {
        throw new Error(
          'Cannot zoom to point cloud: invalid tileset or bounding sphere',
        );
      }
      const bounds = convertBoundingSphereToBounds(tileset.boundingSphere);
      if (!bounds) {
        throw new Error('Could not convert bounding sphere to bounds');
      }
      return Rectangle.fromDegrees(bounds[0], bounds[1], bounds[2], bounds[3]);
    } catch (error) {
      console.error('Error computing point cloud rectangle:', error);
    }
  }

  // Point cloud control methods - work on currently selected tileset
  setPointCloudOpacity(opacity: number) {
    const { pointCloudTool } = this.baseViewer.pointCloudTools;
    const alpha = opacity / 100.0;
    pointCloudTool.pointCloud.setOpacity(alpha);
    this.baseViewer.scene.requestRender();
  }

  setPointCloudPointSize(size: number) {
    const { pointCloudTool } = this.baseViewer.pointCloudTools;
    pointCloudTool.pointCloud.setPointSize(size);
    this.baseViewer.scene.requestRender();
  }

  setPointCloudPointShape(flag: boolean) {
    const { pointCloudTool } = this.baseViewer.pointCloudTools;
    pointCloudTool.pointCloud.setPointShape(flag);
    this.baseViewer.scene.requestRender();
  }

  setPointCloudColor(color: string) {
    const { pointCloudTool } = this.baseViewer.pointCloudTools;
    pointCloudTool.pointCloud.setPointColor(color);
    this.baseViewer.scene.requestRender();
  }

  setPointCloudDensity(density: number) {
    const { pointCloudTool } = this.baseViewer.pointCloudTools;
    pointCloudTool.pointCloud.setPointDensity(density);
    this.baseViewer.scene.requestRender();
  }

  selectPointCloudLayer(id: string) {
    const { pointCloudTool } = this.baseViewer.pointCloudTools;
    return pointCloudTool.pointCloud.getPointCloudById(id);
  }

  removePointCloudLayer(tileset?: Cesium3DTileset) {
    const { pointCloudTool } = this.baseViewer.pointCloudTools;

    pointCloudTool.pointCloud.removePointCloudTileSet(tileset);
  }

  removeAllPointCloudLayers() {
    const { pointCloudTool } = this.baseViewer.pointCloudTools;

    pointCloudTool.pointCloud.removeAllPointCloudTileSets();
  }

  async addCVTilesLayerToCesium(
    s3Key: string,
    layerId: string,
    nodes: LayerNode[],
    imageryLayerParams: ImageryLayerParams,
  ): Promise<CVTilesLayer> {
    const mapLayer = await this.baseViewer.cvTilesTools.cvTilesTool.loadTileset(
      `${EnvVariables.tileServerUrl}/cv-tiles/tileset.json?key=${s3Key}`,
      {
        layerId,
        show: imageryLayerParams.show,
        nodeStyles: buildNodeStylesMap(nodes),
        transformRequest: (url: string) => ({
          url,
          headers: getAuthHeader() as Record<string, string>,
        }),
      },
    );
    return mapLayer;
  }

  removeCadLayer(layer: CVTilesLayer) {
    const { cvTilesTool } = this.baseViewer.cvTilesTools;

    cvTilesTool.removeTileset(layer.layerId);
  }

  addRasterImageryLayer(
    resourceOptions: Resource.ConstructorOptions,
    metadata: ImageryLayerMetadata,
    imageryLayerParams?: ImageryLayerParams,
  ) {
    const urlImageryResource = new Resource({
      url: resourceOptions.url,
      headers: {
        ...getAuthHeader(),
      },
      queryParameters: { ...resourceOptions.queryParameters },
    });

    const urlImageryProvider = new UrlTemplateImageryProvider({
      url: urlImageryResource,
      rectangle: Rectangle.fromDegrees(...metadata.bounds),
      minimumLevel: metadata.minzoom,
      maximumLevel: metadata.maxzoom,
    });

    // TODO: refactor index.
    const addedLayer = this.baseViewer.imageryLayers.addImageryProvider(
      urlImageryProvider,
      imageryLayerParams?.zIndex,
    );

    if (imageryLayerParams?.show) {
      addedLayer.show = imageryLayerParams.show;
    }

    return addedLayer;
  }

  addMbTilesLayerToCesium(
    tilesSrc: FileDataType,
    properties: any,
    imageryLayerParams: ImageryLayerParams,
  ) {
    let cesiumImageryLayer: ImageryProvider | null = null;

    if (isNil(tilesSrc)) {
      throw Error("MbTiles' Layer tiles have not been generated.");
    }

    const baseUrl = !isNil(EnvVariables.tileServerUrl)
      ? `${EnvVariables.tileServerUrl}/mbtiles/{z}/{x}/{y}.png`
      : `${EnvVariables.vectorServerUrl}/{z}/{x}/{y}.png`;
    const imageryResource = new Resource({
      url: baseUrl,
      headers: {
        ...getAuthHeader(),
      },
      queryParameters: { key: tilesSrc.s3Key },
    });

    cesiumImageryLayer = new UrlTemplateImageryProvider({
      url: imageryResource,
      rectangle: properties
        ? Rectangle.fromDegrees(...properties.bounds)
        : undefined,
      minimumLevel: properties?.minzoom,
      maximumLevel: properties?.maxzoom,
    });

    const addedLayer = this.baseViewer.imageryLayers.addImageryProvider(
      cesiumImageryLayer,
      imageryLayerParams.zIndex,
    );
    addedLayer.show = imageryLayerParams.show;

    // For ortho black background.
    addedLayer.colorToAlpha = Color.BLACK;

    return addedLayer;
  }

  addMapboxLayerToCesium(
    sourceId: string,
    imageryLayerParams: ImageryLayerParams,
  ): ImageryLayer | undefined {
    let cesiumImageryLayer: ImageryProvider | null = null;

    if (!isNil(sourceId) && !isEmpty(sourceId)) {
      if (
        EnvVariables.mapBoxUsername &&
        sourceId.includes(EnvVariables.mapBoxUsername)
      ) {
        const mapboxRasterTilesImagerySource = new Resource({
          url: `https://api.mapbox.com/v4/${sourceId}/{z}/{x}/{y}@2x.png`,
          queryParameters: { access_token: EnvVariables.mapBoxAccessToken },
        });

        // TODO: Use MapboxImageryProvider.
        cesiumImageryLayer = new UrlTemplateImageryProvider({
          url: mapboxRasterTilesImagerySource,
          maximumLevel: 22,
        });
      } else {
        cesiumImageryLayer = new MapboxStyleImageryProvider({
          username: 'saitejaus',
          styleId: sourceId,
          accessToken: EnvVariables.mapBoxAccessToken ?? '',
          maximumLevel: 22,
        });
      }
    }

    if (!isNull(cesiumImageryLayer)) {
      const addedLayer = this.baseViewer.imageryLayers.addImageryProvider(
        cesiumImageryLayer,
        imageryLayerParams.zIndex,
      );
      addedLayer.show = imageryLayerParams.show;

      // For ortho black background.
      addedLayer.colorToAlpha = Color.BLACK;

      return addedLayer;
    }
  }

  async addVectorTiledLayerToCesium(
    layer: WorkspaceLayer | SwipeMapLayer,
    imageryLayerParams: ImageryLayerParams,
  ): Promise<ImageryLayer | undefined> {
    const { mapLayerStyles: baseStyle, tiles, properties } = layer;

    if (layer.type === LayerType.Contour && isNil(tiles)) {
      throw Error('Contour layer tiles have not been generated.');
    }

    if (isNil(baseStyle)) {
      return;
    }

    // 🛡️ Deep clone the style before mutation.
    const mapLayerStyles = cloneDeep(baseStyle);

    const baseTileUrl = !isNil(EnvVariables.tileServerUrl)
      ? `${EnvVariables.tileServerUrl}/vector/{z}/{x}/{y}.pbf`
      : `${EnvVariables.vectorServerUrl}/{z}/{x}/{y}.pbf`;

    const queryParam =
      layer.type === LayerType.Contour
        ? `key=${tiles?.s3Key}`
        : `layer_id=${layer.id}`;

    const tileUrl = `${baseTileUrl}?${queryParam}`;

    Object.keys(mapLayerStyles.sources).forEach((styleSourceKey) => {
      mapLayerStyles.sources[styleSourceKey]['tiles'] = [tileUrl];

      if (properties?.bounds) {
        mapLayerStyles.sources[styleSourceKey]['bounds'] = properties.bounds;
      }
    });

    applyHiddenFeatureFiltersToStyle(
      mapLayerStyles,
      imageryLayerParams.hiddenFeatureIds ?? [],
      imageryLayerParams.hideTextboxFeatures ?? false,
    );

    const mvtProvider = new MVTImageryProvider({
      style: mapLayerStyles,
      minzoom: properties?.minzoom ?? 10,
      maxzoom: properties?.maxzoom ?? 25,
      bounds: properties?.bounds,
      transformRequest(url, resourceType) {
        const res = { url };

        if (resourceType == 'Tile') {
          res['headers'] = getAuthHeader();
        }

        return res;
      },
    });

    return mvtProvider.readyPromise.then(() => {
      const addedMvtLayer: ImageryLayer =
        this.baseViewer.imageryLayers.addImageryProvider(
          mvtProvider as unknown as ImageryProvider,
          imageryLayerParams.zIndex,
        );

      addedMvtLayer.show = imageryLayerParams.show;
      return addedMvtLayer;
    });
  }

  addHistogramLayerToCesium(
    histogramValues: HistogramDataType,
    imageryLayerParams: ImageryLayerParams,
  ) {
    const { rescale, opacity, sourceFilePath, metadata } = histogramValues;

    if (isNil(rescale) || isNil(sourceFilePath) || isNil(metadata)) {
      return;
    }

    const baseUrl = !isNil(EnvVariables.tileServerUrl)
      ? `${EnvVariables.tileServerUrl}/histogram/{z}/{x}/{y}.png`
      : `${EnvVariables.rasterServerUrl}/{z}/{x}/{y}.png`;
    const imageryResource = new Resource({
      url: baseUrl,
      headers: {
        ...getAuthHeader(),
      },
      queryParameters: { key: sourceFilePath, rescale },
    });

    const cesiumImageryLayer = new UrlTemplateImageryProvider({
      url: imageryResource,
      rectangle: Rectangle.fromDegrees(...metadata.bounds),
      minimumLevel: metadata.minzoom,
      maximumLevel: metadata.maxzoom,
    });

    if (!isNull(cesiumImageryLayer)) {
      const addedLayer = this.baseViewer.imageryLayers.addImageryProvider(
        cesiumImageryLayer,
        imageryLayerParams.zIndex,
      );

      const currentOpacityValue = !isNil(opacity)
        ? parseFloat(opacity.toString()) / 100.0
        : 100.0;
      addedLayer.show = imageryLayerParams.show;
      addedLayer.alpha = currentOpacityValue;

      return addedLayer;
    }
  }

  addSelfHostedOrtho(orthomosaicCog: FileDataType) {
    let orthoImageryLayer;

    if (orthomosaicCog.properties) {
      orthoImageryLayer = this.addRasterImageryLayer(
        {
          url: `${EnvVariables.tileServerUrl}/ortho/{z}/{x}/{y}.png`,
          queryParameters: { key: orthomosaicCog.s3Key },
        },
        {
          ...orthomosaicCog.properties,
        },
      );
    }

    return orthoImageryLayer;
  }

  // TODO: Remove ION layer & mapbox layer support.
  async addMapLayer({
    layer,
    show,
    addTerrainLayerCallback,
    terrainProvider,
    hiddenFeatureIds,
    hideTextboxFeatures,
  }: AddMapLayerParams) {
    const showMapLayer = show ?? layer.show ?? true;

    let mapLayer: MapLayer | undefined = layer.mapLayer;

    // If map layer exists only toggle the visiblity; no need to add the maplayer.
    if (mapLayer) {
      mapLayer.show = showMapLayer;
    }

    const { type, sourceId, tiles, files, zIndex, cesiumIndex, id } = layer;
    const properties = getLayerMetadata(layer);

    // Use cesiumIndex for Cesium layer positioning, fallback to zIndex if not available.
    const layerIndex = cesiumIndex ?? zIndex;

    switch (type) {
      case LayerType.Cesium:
        if (!terrainProvider && addTerrainLayerCallback && !isNil(sourceId)) {
          const terrainProvider =
            await this.setCesiumIonTerrainProvider(sourceId);

          addTerrainLayerCallback({
            show: showMapLayer,
            terrainProvider,
          });
        }
        break;
      case LayerType.MapBox:
        mapLayer = this.addMapboxLayerToCesium(sourceId || '', {
          show: showMapLayer,
          zIndex: layerIndex,
        });
        break;
      case LayerType.Orthomosaic:
        if (!isEmpty(sourceId)) {
          mapLayer = this.addMapboxLayerToCesium(sourceId || '', {
            show: showMapLayer,
            zIndex: layerIndex,
          });
        } else {
          const orthomosaicCog = files?.find(
            (file) => file.type === FileType.OrthomosaicCog,
          );

          if (orthomosaicCog) {
            mapLayer = this.addSelfHostedOrtho(orthomosaicCog);
          }
        }
        break;
      case LayerType.MBTiles:
        if (!isNil(tiles)) {
          mapLayer = this.addMbTilesLayerToCesium(tiles, properties, {
            show: showMapLayer,
            zIndex: layerIndex,
          });
        }
        break;
      case LayerType.Vector:
      case LayerType.Contour:
        if (type == LayerType.Contour && isNil(tiles)) {
          break;
        }

        mapLayer = await this.addVectorTiledLayerToCesium(layer, {
          show: showMapLayer,
          zIndex: layerIndex,
          hiddenFeatureIds,
          hideTextboxFeatures,
        });
        break;

      // @TODO: Update handling of slope map layer & DSM Layer in workspace-active-layer.
      case LayerType.CapturedDsm:
      case LayerType.SlopeMap:
        if (layer.histogramData) {
          const { metadata, sourceFilePath, rescale, opacity } =
            layer.histogramData;

          mapLayer = this.addHistogramLayerToCesium(
            {
              metadata,
              sourceFilePath,
              rescale,
              opacity: opacity ?? 100,
            },
            {
              show: showMapLayer,
              zIndex: layerIndex,
            },
          );
        }

        break;
      case LayerType.PointCloud:
        mapLayer = await this.addPointCloudLayer(tiles?.s3Key, id, {
          show: showMapLayer,
        });
        break;
      case LayerType.Cad:
        if (!isNil(tiles?.s3Key)) {
          mapLayer = await this.addCVTilesLayerToCesium(
            tiles.s3Key,
            id,
            layer.nodes ?? [],
            {
              show: showMapLayer,
            },
          );
        }
        break;
    }

    return mapLayer;
  }

  removeImageryLayers(layers: MapLayer[]) {
    const imageryLayers = this.baseViewer.imageryLayers;
    layers.forEach((layer) => imageryLayers.remove(layer as ImageryLayer));
  }

  removeAllImageryLayersExceptBase() {
    const imageryLayers = this.baseViewer.imageryLayers;

    for (let index = 2; index < imageryLayers.length; index++) {
      const imageryLayer = imageryLayers.get(index);
      imageryLayer.show = false;
      imageryLayers.remove(imageryLayer);
    }
  }

  resetTerrainProvider() {
    this.setTerrainProvider(new EllipsoidTerrainProvider({}));
  }

  setTerrainProvider(terrainProvider: TerrainProvider) {
    this.baseViewer.terrainProvider = terrainProvider;
  }

  async setCesiumIonTerrainProvider(sourceId: string) {
    const cesiumIonTerrainProvider = await CesiumTerrainProvider.fromIonAssetId(
      +sourceId,
      {
        requestVertexNormals: true,
      },
    );

    this.baseViewer.terrainProvider = cesiumIonTerrainProvider;

    return cesiumIonTerrainProvider;
  }

  raiseImageryLayerToTop(imageryLayer: ImageryLayer) {
    this.baseViewer.imageryLayers.raiseToTop(imageryLayer);
  }

  raiseImageryLayer(imageryLayer: ImageryLayer) {
    this.baseViewer.imageryLayers.raise(imageryLayer);
  }

  lowerImageryLayer(imageryLayer: ImageryLayer) {
    this.baseViewer.imageryLayers.lower(imageryLayer);
  }

  getZIndex(mapLayer?: MapLayer) {
    // Point cloud layers do not have a zIndex.
    if (isNil(mapLayer) || mapLayer instanceof Cesium3DTileset) {
      return undefined;
    }

    const zIndex = Number(
      this.baseViewer.imageryLayers.indexOf(mapLayer as ImageryLayer),
    );
    return zIndex === -1 ? undefined : zIndex;
  }

  getLength() {
    return (
      this.baseViewer.imageryLayers.length +
      this.baseViewer.pointCloudTools.pointCloudTool.getCount()
    );
  }

  getImageryLayerCount() {
    return this.baseViewer.imageryLayers.length;
  }

  getPointCloudCount() {
    return this.baseViewer.pointCloudTools.pointCloudTool.getCount();
  }

  getImageryLayers() {
    return this.baseViewer.imageryLayers;
  }
}
